import type { AdminReconcileFinding, AdminReconcileProvider } from "@admin/lib/api";
import { formatCents } from "@admin/lib/utils";

const USD_CENTS_KINDS = new Set([
  "llm_cost_drift",
  "llm_cache_lifetime_drift",
  "llm_cache_monthly_drift",
  "llm_cache_daily_drift",
  "llm_usage_snapshot",
  "llm_vm_cost_drift",
]);

const CALL_COUNT_KINDS = new Set([
  "composio_count_drift",
  "composio_untracked_billed",
]);

function parseIntValue(value: string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatCalls(n: number): string {
  return n === 1 ? "1 call" : `${n} calls`;
}

function hetznerPriceSuffix(title: string): string {
  if (title === "Volume price catalog drift") return "/GB/mo";
  return "/mo";
}

/** Format a finding cell for the reconciliation table (units depend on kind). */
export function formatFindingValue(
  finding: AdminReconcileFinding,
  column: "internal" | "external" | "diff",
): string {
  const raw =
    column === "internal"
      ? finding.internalValue
      : column === "external"
        ? finding.externalValue
        : finding.diff;

  if (raw == null || raw === "") return "-";

  const { kind, title } = finding;

  if (USD_CENTS_KINDS.has(kind)) {
    const n = parseIntValue(raw);
    if (column === "diff" && n != null) {
      const sign = n > 0 ? "+" : "";
      return `${sign}${formatCents(n)}`;
    }
    return n == null ? raw : formatCents(n);
  }

  if (kind === "hetzner_price_catalog_drift") {
    const n = parseIntValue(raw);
    if (n == null) return raw;
    if (column === "diff") {
      const sign = n > 0 ? "+" : "";
      return `${sign}${formatCents(n)}`;
    }
    return `${formatCents(n)}${hetznerPriceSuffix(title)}`;
  }

  if (CALL_COUNT_KINDS.has(kind)) {
    const n = parseIntValue(raw);
    if (n == null) return raw;
    if (column === "diff") {
      const sign = n > 0 ? "+" : "";
      return `${sign}${formatCalls(Math.abs(n))}`;
    }
    return formatCalls(n);
  }

  if (kind === "composio_premium_split") {
    const n = parseIntValue(raw);
    if (n == null) return raw;
    if (column === "internal") return `${n} premium`;
    if (column === "external") return `${n} standard`;
    return "-";
  }

  if (kind === "hetzner_volume_size_mismatch") {
    const n = parseIntValue(raw);
    return n == null ? raw : `${n} GB`;
  }

  if (kind === "vm_telemetry_silent") {
    if (column === "internal") return raw;
    if (column === "external") return `${raw}h threshold`;
    return "-";
  }

  if (kind === "vm_log_sync_silent") {
    if (column === "internal") return raw;
    if (column === "external") return `${raw}h threshold`;
    return "-";
  }

  if (kind === "slack_bridge_crash_loop") {
    const n = parseIntValue(raw);
    if (n == null) return raw;
    if (column === "internal") return `${n} env-missing lines`;
    if (column === "external") return `${n} wrapper errors`;
    return "-";
  }

  if (kind === "vm_telemetry_optout") {
    return column === "internal" ? "opted out" : "-";
  }

  return raw;
}

/** Short hint for summary cards (provider-level totals). */
export function formatProviderTotals(
  provider: AdminReconcileProvider,
  internal: unknown,
  external: unknown,
): string {
  const i = Number(internal);
  const e = Number(external);
  if (!Number.isFinite(i) || !Number.isFinite(e)) {
    return `Internal: ${String(internal)} / External: ${String(external)}`;
  }
  if (provider === "openrouter") {
    return `Our books: ${formatCents(i)} / LLM provider: ${formatCents(e)}`;
  }
  if (provider === "composio") {
    return `Local ledger: ${formatCalls(i)} / Composio API: ${formatCalls(e)}`;
  }
  if (provider === "telemetry") {
    return `Workspace session cost: ${formatCents(i)} / LLM provider: ${formatCents(e)}`;
  }
  return `Internal: ${String(internal)} / External: ${String(external)}`;
}

export const RECONCILE_VALUE_LEGEND =
  "Internal vs External: our database vs the provider API. Units vary by row: USD/mo (Hetzner catalog), USD (AI spend), or call counts (Composio). Diff = internal minus external.";
